// UltraFlex Admin — Ledgers Explorer (all append-only transaction tables in one place)
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { BookOpen, Search, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const COLUMN_MAP = {
  stock: [
    { k: 'date', l: 'Date', fmt: v => v?.slice(0, 16).replace('T', ' ') },
    { k: 'site', l: 'Site' },
    { k: 'product', l: 'Product' },
    { k: 'movement_type', l: 'Type' },
    { k: 'quantity_change', l: 'Qty Δ', align: 'right' },
    { k: 'balance_after', l: 'Balance', align: 'right' },
    { k: 'reference', l: 'Ref' },
    { k: 'reason', l: 'Reason' },
  ],
  journal: [
    { k: 'date', l: 'Period' },
    { k: 'site', l: 'Site' },
    { k: 'reference', l: 'Ref' },
    { k: 'journal_type', l: 'Journal Type' },
    { k: 'account_code', l: 'Account' },
    { k: 'description', l: 'Description' },
    { k: 'debit', l: 'Debit £', align: 'right', fmt: v => v ? v.toFixed(2) : '' },
    { k: 'credit', l: 'Credit £', align: 'right', fmt: v => v ? v.toFixed(2) : '' },
  ],
  sales: [
    { k: 'date', l: 'Date', fmt: v => v?.slice(0, 16).replace('T', ' ') },
    { k: 'site', l: 'Site' },
    { k: 'txn_number', l: 'Txn #' },
    { k: 'txn_type', l: 'Type' },
    { k: 'status', l: 'Status' },
    { k: 'member', l: 'Member' },
    { k: 'total', l: 'Total £', align: 'right', fmt: v => v?.toFixed(2) },
  ],
  memberships: [
    { k: 'date', l: 'Date', fmt: v => v?.slice(0, 16).replace('T', ' ') },
    { k: 'member_number', l: 'Member #' },
    { k: 'member', l: 'Name' },
    { k: 'payment_type', l: 'Type' },
    { k: 'status', l: 'Status' },
    { k: 'reference', l: 'Ref' },
    { k: 'amount', l: 'Amount £', align: 'right', fmt: v => v?.toFixed(2) },
  ],
  gift_cards: [
    { k: 'date', l: 'Date', fmt: v => v?.slice(0, 16).replace('T', ' ') },
    { k: 'gift_card_id', l: 'Card' },
    { k: 'transaction_type', l: 'Type' },
    { k: 'amount', l: 'Amount £', align: 'right', fmt: v => v?.toFixed(2) },
    { k: 'balance_after', l: 'Balance £', align: 'right', fmt: v => v?.toFixed(2) },
    { k: 'reference', l: 'Ref' },
  ],
  loyalty: [
    { k: 'date', l: 'Date', fmt: v => v?.slice(0, 16).replace('T', ' ') },
    { k: 'member', l: 'Member' },
    { k: 'transaction_type', l: 'Type' },
    { k: 'points', l: 'Points Δ', align: 'right' },
    { k: 'balance_after', l: 'Balance', align: 'right' },
    { k: 'reference', l: 'Ref' },
  ],
  audit: [
    { k: 'date', l: 'Date', fmt: v => v?.slice(0, 16).replace('T', ' ') },
    { k: 'action', l: 'Action' },
    { k: 'entity_type', l: 'Entity' },
    { k: 'actor_type', l: 'Actor' },
    { k: 'description', l: 'Description' },
  ],
  cash_drops: [
    { k: 'date', l: 'Date', fmt: v => v?.slice(0, 16).replace('T', ' ') },
    { k: 'site', l: 'Site' },
    { k: 'drop_type', l: 'Type' },
    { k: 'reason', l: 'Reason' },
    { k: 'amount', l: 'Amount £', align: 'right', fmt: v => v?.toFixed(2) },
  ],
  orders: [
    { k: 'date', l: 'Date', fmt: v => v?.slice(0, 16).replace('T', ' ') },
    { k: 'order_number', l: 'Order #' },
    { k: 'customer', l: 'Customer' },
    { k: 'email', l: 'Email' },
    { k: 'status', l: 'Status' },
    { k: 'payment_status', l: 'Payment' },
    { k: 'total', l: 'Total £', align: 'right', fmt: v => v?.toFixed(2) },
  ],
};

export default function AdminLedgers() {
  const { token } = useAuth();
  const [ledgers, setLedgers] = useState([]);
  const [active, setActive] = useState('stock');
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(new Date(Date.now() - 30 * 86400e3).toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
  const [siteFilter, setSiteFilter] = useState('');
  const [sites, setSites] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch(`${API}/api/ledgers`, { headers }).then(r => r.json()).then(d => setLedgers(d.ledgers || []));
    fetch(`${API}/api/sites`, { headers }).then(r => r.json()).then(d => setSites(d.items || d || []));
  }, []);

  useEffect(() => { fetchLedger(); }, [active, search, dateFrom, dateTo, siteFilter, page]);

  const fetchLedger = async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, page_size: 50, date_from: dateFrom, date_to: dateTo });
    if (search) params.append('search', search);
    if (siteFilter) params.append('site_id', siteFilter);
    const res = await fetch(`${API}/api/ledgers/${active}?${params}`, { headers });
    setData(await res.json());
    setLoading(false);
  };

  const exportCsv = () => {
    if (!data.items?.length) return;
    const cols = COLUMN_MAP[active] || [];
    const headerRow = cols.map(c => c.l).join(',');
    const rows = data.items.map(it => cols.map(c => {
      const v = it[c.k];
      const rendered = c.fmt ? c.fmt(v) : (v ?? '');
      return typeof rendered === 'string' && rendered.includes(',') ? `"${rendered.replace(/"/g, '""')}"` : rendered;
    }).join(',')).join('\n');
    const csv = `${headerRow}\n${rows}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `ledger-${active}-${dateFrom}-to-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const cols = COLUMN_MAP[active] || [];

  return (
    <div className="p-6 min-h-screen bg-uf-bg">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Ledgers Explorer</h1>
          <p className="text-uf-textMuted text-sm mt-1">Every transaction table. Searchable. Exportable.</p>
        </div>
        <button onClick={exportCsv} disabled={!data.items?.length} data-testid="ledger-export-csv" className="px-3 py-2 bg-uf-red text-white rounded text-sm flex items-center gap-2 disabled:opacity-40"><Download className="w-4 h-4" /> Export CSV</button>
      </div>

      {/* Ledger tabs */}
      <div className="flex flex-wrap gap-2 mb-4 border-b border-uf-border pb-3">
        {ledgers.map(l =>
          <button key={l.key} onClick={() => { setActive(l.key); setPage(1); }} data-testid={`ledger-tab-${l.key}`}
                  className={`px-3 py-2 text-xs font-medium rounded flex items-center gap-2 ${active === l.key ? 'bg-uf-red text-white' : 'bg-uf-surface border border-uf-border text-uf-textMuted hover:text-white'}`}>
            <BookOpen className="w-3 h-3" /> {l.name} <span className="opacity-70">({l.count?.toLocaleString()})</span>
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-uf-textMuted" />
          <input placeholder="Search..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} data-testid="ledger-search" className="w-full bg-uf-surface border border-uf-border pl-10 pr-4 py-2 text-white text-sm rounded" />
        </div>
        <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} data-testid="ledger-date-from" className="bg-uf-surface border border-uf-border px-3 py-2 text-white text-sm rounded" />
        <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} data-testid="ledger-date-to" className="bg-uf-surface border border-uf-border px-3 py-2 text-white text-sm rounded" />
        <select value={siteFilter} onChange={e => { setSiteFilter(e.target.value); setPage(1); }} data-testid="ledger-site-filter" className="bg-uf-surface border border-uf-border px-3 py-2 text-white text-sm rounded">
          <option value="">All sites</option>
          {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-uf-surface border border-uf-border rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-uf-bg text-uf-textMuted text-xs uppercase tracking-wider">
              {cols.map(c => <th key={c.k} className={`px-3 py-2 ${c.align === 'right' ? 'text-right' : 'text-left'} font-bold`}>{c.l}</th>)}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={cols.length} className="p-8 text-center text-uf-textMuted">Loading...</td></tr>}
            {!loading && data.items?.length === 0 && <tr><td colSpan={cols.length} className="p-8 text-center text-uf-textMuted">No entries in range</td></tr>}
            {data.items?.map((it, i) =>
              <tr key={it.id || i} data-testid={`ledger-row-${i}`} className="border-t border-uf-border hover:bg-uf-bg">
                {cols.map(c => {
                  const v = it[c.k];
                  const rendered = c.fmt ? c.fmt(v) : v;
                  return <td key={c.k} className={`px-3 py-2 ${c.align === 'right' ? 'text-right font-mono' : ''} text-white whitespace-nowrap`}>{rendered ?? '—'}</td>;
                })}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {data.total_pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-uf-textMuted text-sm">Page {data.page} of {data.total_pages} · {data.total?.toLocaleString()} total entries</div>
          <div className="flex gap-2">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="px-3 py-1.5 bg-uf-surface border border-uf-border text-white rounded disabled:opacity-40">Previous</button>
            <button onClick={() => setPage(Math.min(data.total_pages, page + 1))} disabled={page >= data.total_pages} className="px-3 py-1.5 bg-uf-surface border border-uf-border text-white rounded disabled:opacity-40">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
